/**
 * Adapters for the top-level forecast, the metrics, and the small forecast helpers (spread into
 * `registry`). They only translate JSON ⇄ domain values; output shapes mirror ContractRegistry.swift.
 */
import { forecast } from './api.ts';
import { formatDay } from './core/calendar.ts';
import { CASHFLOW_ORDER, type CashflowForecast, type CashflowType } from './calculations/cashflow.ts';
import {
  calculateMetrics, CUMULATIVE_ORDER, DEFAULT_METRICS_INPUTS, type CumulativeSeries, type ForecastMetrics, type MetricsCashflow,
} from './calculations/metrics.ts';
import { estimatePeriodChange } from './calculations/period-change.ts';
import {
  decodeAsset, decodeCheckpoint, decodeDepreciatingAsset, decodePlan, decodeProperty, decodeSeries, decodeTransactions,
  encodeBucketGroup, encodeWindow,
} from './codec/entities.ts';
import { contextFrom, decodeDay, decodeDecimal, encodeDecimal, encodeOptionalDay, optionalDay, optionalDecimal } from './codec/scalars.ts';
import { yearlySamples } from './forecast/payment-bucket.ts';
import { encodeGoalProgress } from './registry-investments-goals.ts';
import type { Registry } from './registry.ts';
import type { Series } from './types/domain.ts';
import type * as Wire from './types/contract.generated.ts';

type WireGroup = Readonly<Partial<Record<string, Wire.Series>>> | null | undefined;

/** Only the series present in the input; absent ones read as empty downstream, as in ContractInput. */
function decodeNamed<K extends string>(names: readonly K[], group: WireGroup): Partial<Record<K, Series>> {
  return Object.fromEntries(
    names.filter((name) => group?.[name] !== undefined).map((name) => [name, decodeSeries(group![name])]),
  ) as Partial<Record<K, Series>>;
}

const decodeCashflow = (c: Wire.CashflowInput | null | undefined): MetricsCashflow | null =>
  c === undefined || c === null
    ? null
    : { payments: decodeNamed(CASHFLOW_ORDER, c.payments), windowed: decodeNamed(CASHFLOW_ORDER, c.windowed) };

const encodeCashflowGroup = (series: Readonly<Record<CashflowType, Series>>): Wire.BucketGroup =>
  encodeBucketGroup(CASHFLOW_ORDER.map((type) => [type, series[type]] as const));

const encodeCashflow = (c: CashflowForecast): Wire.CashflowOutput => ({
  calculationWindow: encodeWindow(c.calculationWindow),
  payments: encodeCashflowGroup(c.payments),
  windowed: encodeCashflowGroup(c.windowed),
});

const encodeCumulative = (c: CumulativeSeries): Wire.BucketGroup =>
  encodeBucketGroup(CUMULATIVE_ORDER.map((name) => [name, c[name]] as const));

const encodeMetrics = (m: ForecastMetrics): Wire.Metrics => ({
  currentNetWorth: encodeDecimal(m.currentNetWorth),
  currentCash: encodeDecimal(m.currentCash),
  totalInvestments: encodeDecimal(m.totalInvestments),
  totalDebt: encodeDecimal(m.totalDebt),
  currentPropertyEquity: encodeDecimal(m.currentPropertyEquity),
  monthlySavings: encodeDecimal(m.monthlySavings),
  projectedPassiveIncome: encodeDecimal(m.projectedPassiveIncome),
  finalNetWorth: encodeDecimal(m.finalNetWorth),
  finalCash: encodeDecimal(m.finalCash),
  finalInvestment: encodeDecimal(m.finalInvestment),
  finalDebt: encodeDecimal(m.finalDebt),
  finalPropertyEquity: encodeDecimal(m.finalPropertyEquity),
  finalPropertyValue: encodeDecimal(m.finalPropertyValue),
  finalMortgageBalance: encodeDecimal(m.finalMortgageBalance),
  propertyPurchaseEquity: encodeDecimal(m.propertyPurchaseEquity),
  propertyGrowth: encodeDecimal(m.propertyGrowth),
  peakNetWorth: encodeDecimal(m.peakNetWorth),
  peakNetWorthDate: encodeOptionalDay(m.peakNetWorthDate),
  highestCash: encodeDecimal(m.highestCash),
  highestCashDate: encodeOptionalDay(m.highestCashDate),
  lowestCash: encodeDecimal(m.lowestCash),
  lowestCashDate: encodeOptionalDay(m.lowestCashDate),
  debtFreeDate: encodeOptionalDay(m.debtFreeDate),
  timeToDebtFreeMonths: m.timeToDebtFreeMonths,
  totalIncome: encodeDecimal(m.totalIncome),
  totalInvestmentContributions: encodeDecimal(m.totalInvestmentContributions),
  totalExpenses: encodeDecimal(m.totalExpenses),
  totalDebtPayments: encodeDecimal(m.totalDebtPayments),
  netSavings: encodeDecimal(m.netSavings),
  averagePassiveIncome: encodeDecimal(m.averagePassiveIncome),
  savingsRate: encodeDecimal(m.savingsRate),
});

export const forecastRegistry: Registry = {
  'ForecastEngine.calculateForecast': (input) => {
    const plan = decodePlan(input.plan);
    const result = forecast(plan, {
      asOf: contextFrom(input).now,
      checkpoint: input.checkpoint === undefined || input.checkpoint === null ? null : decodeCheckpoint(input.checkpoint),
      mortgageIdsTreatedAsDebt: input.mortgageIdsTreatedAsDebt ?? [],
    });
    const potentialIds = plan.transactions.filter((t) => t.isActive && (t.type === 'debt' || t.type === 'expense')).map((t) => t.id);
    return {
      window: encodeWindow(result.window),
      cashflow: encodeCashflow(result.cashflow),
      cumulative: encodeCumulative(result.cumulative),
      potential: potentialIds.flatMap((id) => {
        const p = result.cumulative.potential.get(id);
        return p === undefined ? [] : [{ id, interestTotal: encodeDecimal(p.interestTotal), total: encodeDecimal(p.total) }];
      }),
      metrics: encodeMetrics(result.metrics),
      goals: result.goals.map(encodeGoalProgress),
      now: {
        cash: encodeDecimal(result.now.cash),
        investments: encodeDecimal(result.now.investments),
        debt: encodeDecimal(result.now.debt),
        propertyEquity: encodeDecimal(result.now.propertyEquity),
        assetValue: encodeDecimal(result.now.assetValue),
        netWorth: encodeDecimal(result.now.netWorth),
      },
    };
  },

  'MetricsCalculator.calculate': (input) =>
    encodeMetrics(
      calculateMetrics(
        {
          cumulative: {
            ...decodeNamed(CUMULATIVE_ORDER, input.cumulative as WireGroup),
            debtFreeDate: optionalDay(input.cumulative?.debtFreeDate),
          },
          cashflow: decodeCashflow(input.cashflow),
          initialBalance: optionalDecimal(input.initialBalance) ?? DEFAULT_METRICS_INPUTS.initialBalance,
          transactions: input.transactions === undefined || input.transactions === null ? null : decodeTransactions(input.transactions),
          properties: (input.properties ?? []).map(decodeProperty),
          assets: (input.assets ?? []).map(decodeAsset),
          depreciatingAssets: (input.depreciatingAssets ?? []).map(decodeDepreciatingAsset),
          expectedReturnPercent: optionalDecimal(input.expectedReturnPercent) ?? DEFAULT_METRICS_INPUTS.expectedReturnPercent,
          forecastEndDate: optionalDay(input.forecastEndDate),
          cashSinceDate: optionalDay(input.cashSinceDate),
          investmentBaseline: input.investmentBaseline === undefined || input.investmentBaseline === null
            ? null
            : { investments: decodeDecimal(input.investmentBaseline.investments), since: decodeDay(input.investmentBaseline.since) },
        },
        contextFrom(input),
      ),
    ),

  'PaymentBucket.yearlySamples': (input) => ({
    samples: yearlySamples(decodeSeries(input.series), input.years).map((s) => ({ year: s.year, value: encodeDecimal(s.value) })),
  }),

  'PeriodChangeEstimator.estimate': (input) => {
    const e = estimatePeriodChange(decodeTransactions(input.transactions), decodeDecimal(input.investmentBalance), contextFrom(input));
    return {
      periodStart: formatDay(e.periodStart),
      periodEnd: formatDay(e.periodEnd),
      daysRemaining: e.daysRemaining,
      cashChange: encodeDecimal(e.cashChange),
      investmentGrowth: encodeDecimal(e.investmentGrowth),
      totalChange: encodeDecimal(e.totalChange),
    };
  },
};
